const configuredBase = process.env.REACT_APP_DATA_BASE_URL?.replace(/\/$/, "");
export const DATA_BASE = configuredBase || "https://pub-1222c11b12ed4556aafe903d8e6a52c7.r2.dev/data";

export async function fetchPublicJson(file, { signal, timeoutMs = 10000 } = {}) {
  if (!/^[a-zA-Z0-9_/-]+\.json$/.test(file) || file.includes("..")) throw new Error("Invalid data path");
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  let timer;
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(`${DATA_BASE}/${file}`, { signal: controller.signal, cache: "no-cache" });
        if (!response.ok) {
          const error = new Error(`Data HTTP ${response.status}`);
          error.status = response.status;
          throw error;
        }
        return response.json();
      })(),
      new Promise((_, reject) => {
        timer = setTimeout(() => { controller.abort(); reject(new Error("Data request timed out")); }, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

export function validArticles(value) {
  if (!Array.isArray(value) || value.length > 400) return false;
  const ids = new Set();
  return value.every((a) => {
    if (!a || typeof a.id !== "string" || ids.has(a.id) ||
        typeof a.neutral_title !== "string" || typeof a.original_url !== "string" ||
        typeof a.neutral_content !== "string" || !Number.isFinite(Date.parse(a.published_at))) return false;
    ids.add(a.id);
    return true;
  });
}

export function validFeed(value) {
  return value?.schema_version === 1 && value.generation === value.generated_at &&
    Number.isFinite(Date.parse(value.generated_at)) && validArticles(value.articles) &&
    value.stats?.generated_at === value.generated_at && value.stats?.total_articles === value.articles.length;
}

function sameJsonArray(left, right) {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  return JSON.stringify(left) === JSON.stringify(right);
}

function sameArticleRevision(left, right) {
  if (!left || !right) return false;
  const scalarFields = [
    "id", "original_title", "original_url", "neutral_title", "neutral_content", "tldr",
    "source", "category", "image_url", "published_at", "processed_at", "processor",
    "processor_version", "source_char_count", "source_paragraph_count", "source_count",
    "quality_score", "has_detail", "detail_file", "repair_pending",
  ];
  return scalarFields.every((field) => left[field] === right[field]) &&
    sameJsonArray(left.original_articles || [], right.original_articles || []) &&
    sameJsonArray(left.content_blocks || [], right.content_blocks || []) &&
    sameJsonArray(left.quality_flags || [], right.quality_flags || []);
}

export function reuseUnchangedArticles(previousArticles, nextArticles) {
  if (!Array.isArray(previousArticles) || !Array.isArray(nextArticles)) return nextArticles;
  const previousById = new Map(previousArticles.map((article) => [article.id, article]));
  const reconciled = nextArticles.map((article) => {
    const previous = previousById.get(article.id);
    return sameArticleRevision(previous, article) ? previous : article;
  });
  return reconciled.length === previousArticles.length &&
    reconciled.every((article, index) => article === previousArticles[index])
    ? previousArticles
    : reconciled;
}

export function readStored(key, fallback, validate = () => true) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return parsed !== null && validate(parsed) ? parsed : fallback;
  } catch { return fallback; }
}

export function writeStored(key, value) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch { /* Storage is optional, including private browsing and quota exhaustion. */ }
}

export function mergeArticleDetail(article, detail) {
  if (detail?.id !== article.id || detail.processed_at !== article.processed_at ||
      typeof detail.neutral_content !== "string" || detail.neutral_content.length < article.neutral_content.length) return article;
  return { ...article, neutral_content: detail.neutral_content,
    content_blocks: Array.isArray(detail.content_blocks) ? detail.content_blocks : article.content_blocks };
}

export function matchesArticle(article, query) {
  const originals = Array.isArray(article.original_articles) ? article.original_articles : [];
  const text = [article.neutral_title, article.original_title, article.neutral_content, article.tldr,
    article.source, article.category, ...originals.flatMap((a) => [a.source, a.title])];
  return text.filter(Boolean).join(" ").toLocaleLowerCase("da").includes(query.trim().toLocaleLowerCase("da"));
}
